import { withOnClickTracking } from 'auto-tracking';
import { styled, theme } from 'design-system';
import { useTranslation } from 'next-i18next';
import Image from 'next/image';
import { useRouter } from 'next/router';
import { useEffect, useState } from 'react';

import { ChecklistState } from '../../checklist/use-checklist-state';
import {
  checkFeedbackExists,
  type FeedbackRating,
  type FeedbackSection,
  requestFollowUp,
  submitFeedback,
} from '../../utils/feedback';
import { CheckboxInputNew } from '../inputs/checkbox-input-new';

interface FeedbackWidgetProps {
  checklistState?: ChecklistState;
  submissionState?: { name: string };
}

const FeedbackContainer = styled('div', {
  background: theme.colors.gray1,
  borderRadius: 10,
  boxShadow: '0 4px 24px rgba(0,0,0,0.08)',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  position: 'fixed',
  bottom: 110,
  right: 24,
  zIndex: 1000,
  transition: 'all 0.4s cubic-bezier(0.34, 1.56, 0.64, 1)',
  transform: 'scale(1) translateY(0)',
  opacity: 1,
  transformOrigin: 'bottom center',
  variants: {
    step: {
      select: {
        padding: '10px 0px',
        width: '110px',
      },
      other: {
        padding: '15px 20px',
        width: '280px',
      },
      email: {
        padding: '20px 24px',
        width: '390px',
        background:
          'linear-gradient(135deg, rgba(255,255,255,0.9) 0%, rgba(248,250,252,0.95) 100%)',
        backdropFilter: 'blur(10px)',
        border: '1px solid rgba(255,255,255,0.2)',
        boxShadow:
          '0 20px 40px rgba(0, 0, 0, 0.1), 0 8px 32px rgba(0, 98, 255, 0.08), 0 4px 16px rgba(0, 0, 0, 0.05)',
        animation: 'slideInUp 0.6s cubic-bezier(0.34, 1.56, 0.64, 1)',
      },
    },
  },
  '@keyframes slideInUp': {
    '0%': {
      transform: 'translateY(30px) scale(0.9)',
      opacity: 0,
    },
    '100%': {
      transform: 'translateY(0) scale(1)',
      opacity: 1,
    },
  },
});

const InnerSection = styled('div', {
  width: '110px',
});

const CloseButton = styled('button', {
  position: 'absolute',
  top: -20,
  right: -6,
  background: 'none',
  border: 'none',
  padding: 0,
  fontSize: 28,
  cursor: 'pointer',
  color: theme.colors.gray9,
  transition: 'all 0.2s ease',
  '&:hover': {
    color: theme.colors.blue11,
  },
  zIndex: 1001,
});

const Title = styled('div', {
  fontWeight: 600,
  fontSize: 12,
  textAlign: 'center',
  marginBottom: 10,
  color: 'rgba(5, 30, 87, 1)',
  width: '80%',
  margin: '0 auto 10px auto',
});

const OptionsColumn = styled('div', {
  display: 'flex',
  flexDirection: 'column',
  gap: 10,
  width: '100%',
});

const OptionButton = styled('button', {
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  background: 'transparent',
  border: 'none',
  borderRadius: 10,
  padding: '10px 15px',
  cursor: 'pointer',
  fontSize: 12,
  fontWeight: 500,
  color: 'rgba(5, 30, 87, 1)',
  transition: 'background 0.2s, outline 0.2s',
  variants: {
    selected: {
      true: {
        background: 'rgba(217, 217, 217, 0.3)',
        outline: `2px solid ${theme.colors.blue8}`,
        boxShadow: '0 2px 8px 0 rgba(0,0,0,0.04)',
      },
      false: {
        outline: 'none',
      },
    },
  },
  '&:hover': {
    marginLeft: '6px',
    marginRight: '6px',
    background: 'rgba(217, 217, 217, 0.3)',
  },
});

const ThankYouTitle = styled('div', {
  fontWeight: 600,
  fontSize: 14,
  marginBottom: 8,
  color: 'rgba(5, 30, 87, 1)',
  textAlign: 'center',
});

const ThankYouSubtitle = styled('div', {
  color: 'rgba(93, 106, 129, 1)',
  fontSize: 12,
  marginBottom: 24,
  maxWidth: 185,
  textAlign: 'center',
});

const ThankYouButton = styled('button', {
  width: '100%',
  padding: '10px 19px',
  borderRadius: 8,
  border: `1px solid rgba(0, 98, 255, 1)`,
  background: 'transparent',
  color: 'rgba(0, 98, 255, 1)',
  fontWeight: 600,
  fontSize: 14,
  marginBottom: 12,
  cursor: 'pointer',
  transition: 'background 0.2s',
  '&:hover': {
    background: theme.colors.blue3,
  },
});

const ThankYouSecondary = styled('button', {
  width: '100%',
  padding: '10px 0',
  borderRadius: 12,
  border: 'none',
  background: 'transparent',
  color: 'rgba(0, 98, 255, 1)',
  fontWeight: 600,
  lineHeight: '20px',
  fontSize: 14,
  cursor: 'pointer',
});

const FeedbackFormTitle = styled('div', {
  fontWeight: 600,
  fontSize: 14,
  marginBottom: 8,
  color: 'rgba(5, 30, 87, 1)',
  textAlign: 'left',
  width: '100%',
});

const FeedbackFormLabel = styled('div', {
  color: 'rgba(5, 30, 87, 1)',
  fontSize: 12,
  marginBottom: 8,
  fontWeight: 500,
  textAlign: 'left',
  width: '100%',
});

const FeedbackTextarea = styled('textarea', {
  width: '100%',
  minHeight: 200,
  borderRadius: 4,
  border: `1px solid rgba(226, 232, 241, 1)`,
  padding: 16,
  fontSize: 12,
  color: 'rgba(93, 106, 129, 1)',
  fontWeight: 500,
  marginBottom: 24,
  resize: 'vertical',
  outline: 'none',
  backgroundColor: 'rgba(243, 243, 245, 1)',
  '&:focus': {
    borderColor: theme.colors.blue8,
  },
});

const SubmitButton = styled('button', {
  width: '100%',
  padding: '10px 19px',
  borderRadius: 8,
  border: `1px solid rgba(0, 98, 255, 1)`,
  background: 'transparent',
  color: 'rgba(0, 98, 255, 1)',
  fontWeight: 600,
  fontSize: 14,
  marginBottom: 12,
  cursor: 'pointer',
  transition: 'background 0.2s',
  '&:hover': {
    background: theme.colors.blue3,
  },
});

const ToastContainer = styled('div', {
  position: 'fixed',
  bottom: 24,
  right: 24,
  zIndex: 1000,
  borderRadius: 10,
  boxShadow: '0 2px 12px 0 rgba(0,0,0,0.08)',
  background: 'White',
  padding: '15px 24px',
  display: 'flex',
  alignItems: 'center',
  gap: 5,
  fontSize: 14,
  fontWeight: 600,
  color: 'rgba(5, 30, 87, 1)',
});

const ToastClose = styled('button', {
  background: 'none',
  border: 'none',
  fontSize: 18,
  color: theme.colors.gray11,
  cursor: 'pointer',
});

const EmailContainer = styled('div', {
  width: '100%',
  display: 'flex',
  flexDirection: 'column',
  gap: 16,
  animation: 'fadeInUp 0.5s ease-out',
  '@keyframes fadeInUp': {
    '0%': {
      opacity: 0,
      transform: 'translateY(10px)',
    },
    '100%': {
      opacity: 1,
      transform: 'translateY(0)',
    },
  },
});

const EmailTitle = styled('div', {
  fontWeight: 600,
  fontSize: 16,
  color: 'rgba(5, 30, 87, 1)',
  textAlign: 'center',
  marginBottom: 4,
  background:
    'linear-gradient(135deg, rgba(0, 98, 255, 1) 0%, rgba(59, 130, 246, 1) 100%)',
  backgroundClip: 'text',
  '-webkit-background-clip': 'text',
  '-webkit-text-fill-color': 'transparent',
  animation: 'shimmer 2s ease-in-out infinite',
  '@keyframes shimmer': {
    '0%, 100%': {
      transform: 'translateX(0)',
    },
    '50%': {
      transform: 'translateX(2px)',
    },
  },
});

const EmailSubtitle = styled('div', {
  color: 'rgba(93, 106, 129, 1)',
  fontSize: 12,
  textAlign: 'center',
  marginBottom: 12,
  lineHeight: '16px',
  fontWeight: 500,
});

const EmailInput = styled('input', {
  width: '100%',
  padding: '14px 16px',
  borderRadius: 12,
  border: '2px solid rgba(226, 232, 241, 1)',
  fontSize: 14,
  color: 'rgba(5, 30, 87, 1)',
  backgroundColor: 'rgba(255, 255, 255, 0.8)',
  outline: 'none',
  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
  fontWeight: 500,
  '&:focus': {
    borderColor: theme.colors.blue8,
    backgroundColor: 'white',
    transform: 'translateY(-2px)',
    boxShadow:
      '0 8px 25px rgba(0, 98, 255, 0.15), 0 3px 10px rgba(0, 0, 0, 0.1)',
  },
  '&::placeholder': {
    color: 'rgba(93, 106, 129, 0.7)',
    fontWeight: 400,
  },
  '&:hover': {
    borderColor: 'rgba(0, 98, 255, 0.4)',
    transform: 'translateY(-1px)',
  },
  variants: {
    state: {
      error: {
        borderColor: theme.colors.red8,
        backgroundColor: 'rgba(248, 113, 113, 0.05)',
        '&:focus': {
          borderColor: theme.colors.red8,
          backgroundColor: 'rgba(248, 113, 113, 0.05)',
        },
      },
    },
  },
});

const CheckboxContainer = styled('div', {
  display: 'flex',
  alignItems: 'flex-start',
  gap: 12,
  marginBottom: 8,
  padding: '12px',
  borderRadius: 10,
  backgroundColor: 'rgba(0, 98, 255, 0.02)',
  border: '1px solid rgba(0, 98, 255, 0.1)',
  transition: 'all 0.2s ease',
  '&:hover': {
    backgroundColor: 'rgba(0, 98, 255, 0.04)',
    border: '1px solid rgba(0, 98, 255, 0.2)',
  },
});

const EmailButtonContainer = styled('div', {
  display: 'flex',
  flexDirection: 'column',
  gap: 8,
});

const EmailSubmitButton = styled('button', {
  width: '100%',
  padding: '14px 24px',
  borderRadius: 12,
  border: 'none',
  background:
    'linear-gradient(135deg, rgba(0, 98, 255, 1) 0%, rgba(59, 130, 246, 1) 100%)',
  color: 'white',
  fontWeight: 600,
  fontSize: 14,
  cursor: 'pointer',
  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
  position: 'relative',
  overflow: 'hidden',
  '&:hover': {
    transform: 'translateY(-2px)',
    boxShadow:
      '0 10px 25px rgba(0, 98, 255, 0.3), 0 5px 15px rgba(0, 0, 0, 0.1)',
    background:
      'linear-gradient(135deg, rgba(0, 85, 220, 1) 0%, rgba(45, 108, 223, 1) 100%)',
  },
  '&:active': {
    transform: 'translateY(0)',
  },
  '&:disabled': {
    opacity: 0.4,
    cursor: 'not-allowed',
    transform: 'none',
    background:
      'linear-gradient(135deg, rgba(156, 163, 175, 1) 0%, rgba(156, 163, 175, 1) 100%)',
    color: 'rgba(255, 255, 255, 0.8)',
    '&:hover': {
      transform: 'none',
      boxShadow: 'none',
      background:
        'linear-gradient(135deg, rgba(156, 163, 175, 1) 0%, rgba(156, 163, 175, 1) 100%)',
    },
    '&::before': {
      display: 'none',
    },
  },
  '&::before': {
    content: '""',
    position: 'absolute',
    top: 0,
    left: '-100%',
    width: '100%',
    height: '100%',
    background:
      'linear-gradient(90deg, transparent, rgba(255,255,255,0.2), transparent)',
    transition: 'left 0.5s',
  },
  '&:hover::before': {
    left: '100%',
  },
});

const EmailIcon = styled('div', {
  fontSize: 24,
  marginBottom: 8,
  textAlign: 'center',
  animation: 'bounce 2s ease-in-out infinite',
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  '@keyframes bounce': {
    '0%, 20%, 50%, 80%, 100%': {
      transform: 'translateY(0)',
    },
    '40%': {
      transform: 'translateY(-5px)',
    },
    '60%': {
      transform: 'translateY(-3px)',
    },
  },
});

const EmailErrorMessage = styled('div', {
  color: theme.colors.red8,
  fontSize: 10,
  marginTop: 2,
  marginBottom: 0,
  fontWeight: 400,
  textAlign: 'left',
  lineHeight: '12px',
  animation: 'fadeIn 0.3s ease',
  '@keyframes fadeIn': {
    '0%': {
      opacity: 0,
      transform: 'translateY(-3px)',
    },
    '100%': {
      opacity: 1,
      transform: 'translateY(0)',
    },
  },
});

const EmailHelperText = styled('div', {
  color: 'rgba(93, 106, 129, 0.8)',
  fontSize: 11,
  marginTop: 4,
  marginBottom: 8,
  fontWeight: 400,
  textAlign: 'center',
  fontStyle: 'italic',
  animation: 'fadeIn 0.3s ease',
  '@keyframes fadeIn': {
    '0%': {
      opacity: 0,
      transform: 'translateY(-3px)',
    },
    '100%': {
      opacity: 1,
      transform: 'translateY(0)',
    },
  },
});

const ChatbotIcon = styled('button', {
  position: 'fixed',
  bottom: 110,
  right: 24,
  zIndex: 1001,
  borderRadius: '50%',
  background: theme.colors.white,
  border: 'none',
  cursor: 'pointer',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  fontSize: 24,
  color: 'white',

  opacity: 1,
});

const TrackedCloseButton = withOnClickTracking(CloseButton);
const TrackedOptionButton = withOnClickTracking(OptionButton);
const TrackedThankYouButton = withOnClickTracking(ThankYouButton);
const TrackedThankYouSecondary = withOnClickTracking(ThankYouSecondary);
const TrackedSubmitButton = withOnClickTracking(SubmitButton);
const TrackedToastClose = withOnClickTracking(ToastClose);
const TrackedChatbotIcon = withOnClickTracking(ChatbotIcon);
const TrackedEmailSubmitButton = withOnClickTracking(EmailSubmitButton);

export function FeedbackWidget({
  checklistState,
  submissionState,
}: FeedbackWidgetProps = {}) {
  const router = useRouter();
  const documentId = router.query.documentId as string;
  const r = useTranslation('results').t;

  const FEEDBACK_OPTIONS = [
    {
      emoji: '🤗',
      label: r('feedbackWidget.ratingOptions.outstanding'),
      value: 'OUTSTANDING' as FeedbackRating,
    },
    {
      emoji: '🙂',
      label: r('feedbackWidget.ratingOptions.acceptable'),
      value: 'ACCEPTABLE' as FeedbackRating,
    },
    {
      emoji: '😕',
      label: r('feedbackWidget.ratingOptions.notAcceptable'),
      value: 'NOT_ACCEPTABLE' as FeedbackRating,
    },
  ];

  const getFeedbackFormContent = (rating: FeedbackRating) => {
    switch (rating) {
      case 'OUTSTANDING':
        return {
          title: r('feedbackWidget.feedbackForm.outstanding.title'),
          label: r('feedbackWidget.feedbackForm.outstanding.label'),
          placeholder: r('feedbackWidget.feedbackForm.outstanding.placeholder'),
        };
      case 'ACCEPTABLE':
        return {
          title: r('feedbackWidget.feedbackForm.acceptable.title'),
          label: r('feedbackWidget.feedbackForm.acceptable.label'),
          placeholder: r('feedbackWidget.feedbackForm.acceptable.placeholder'),
        };
      case 'NOT_ACCEPTABLE':
        return {
          title: r('feedbackWidget.feedbackForm.notAcceptable.title'),
          label: r('feedbackWidget.feedbackForm.notAcceptable.label'),
          placeholder: r(
            'feedbackWidget.feedbackForm.notAcceptable.placeholder',
          ),
        };
      default:
        return {
          title: '',
          label: '',
          placeholder: '',
        };
    }
  };

  const [step, setStep] = useState<
    'select' | 'thankyou' | 'form' | 'email' | 'toast' | 'minimized'
  >('select');
  const [selected, setSelected] = useState<FeedbackRating | null>(null);
  const [feedback, setFeedback] = useState('');
  const [email, setEmail] = useState('');
  const [emailError, setEmailError] = useState('');
  const [wantsEmailUpdates, setWantsEmailUpdates] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isVisible, setIsVisible] = useState(false);
  const [statesReadyTime, setStatesReadyTime] = useState<number | null>(null);
  const [isChatbotHovered, setIsChatbotHovered] = useState(false);

  const isValidEmail = (email: string) => {
    if (!email || email.trim().length === 0) {
      return false;
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      return false;
    }

    const trimmedEmail = email.trim();

    if (trimmedEmail.length < 5 || trimmedEmail.length > 254) {
      return false;
    }

    const validChars =
      /^[a-zA-Z0-9.!#$%&'*+/=?^_`{|}~-]+@[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?(?:\.[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?)*$/;
    if (!validChars.test(trimmedEmail)) {
      return false;
    }

    const parts = trimmedEmail.split('@');
    if (parts.length !== 2) {
      return false;
    }

    const localPart = parts[0];
    const domainPart = parts[1];

    if (!localPart || !domainPart) {
      return false;
    }

    if (localPart.length > 64) {
      return false;
    }
    if (localPart.startsWith('.') || localPart.endsWith('.')) {
      return false;
    }
    if (localPart.includes('..')) {
      return false;
    }

    if (domainPart.length > 253) {
      return false;
    }
    if (domainPart.startsWith('-') || domainPart.endsWith('-')) {
      return false;
    }
    if (!domainPart.includes('.')) {
      return false;
    }

    return true;
  };

  const getEmailValidationMessage = (email: string) => {
    if (!email || email.trim().length === 0) {
      return '';
    }
    if (!isValidEmail(email)) {
      return r('feedbackWidget.emailScreen.validationMessages.invalid');
    }
    return '';
  };

  const isEmailSubmissionDisabled = () => {
    if (isSubmitting) {
      return true;
    }

    if (emailError) {
      return true;
    }

    if (email.trim() && !isValidEmail(email.trim())) {
      return true;
    }

    if (wantsEmailUpdates) {
      const trimmedEmail = email.trim();
      if (!trimmedEmail || !isValidEmail(trimmedEmail)) {
        return true;
      }
    }

    return false;
  };

  const getSubmitButtonHelperText = () => {
    return '';
  };

  const isSubmissionReady =
    checklistState?.name === 'done' &&
    submissionState?.name !== 'UNKNOWN' &&
    submissionState?.name !== 'VOUCHER_PENDING' &&
    submissionState?.name !== 'PROCESSING_FAILED' &&
    submissionState?.name !== 'PROCESSING_FAILED_RETRYING' &&
    submissionState?.name !== 'ERROR' &&
    submissionState?.name !== 'ERROR_RETRYING';

  useEffect(() => {
    if (isSubmissionReady && statesReadyTime === null) {
      const readyTime = Date.now();
      setStatesReadyTime(readyTime);
    }
  }, [isSubmissionReady, statesReadyTime]);

  useEffect(() => {
    let timeoutId: NodeJS.Timeout;

    const checkAndShowWidget = async () => {
      if (!documentId) {
        return;
      }

      if (!isSubmissionReady) {
        return;
      }

      if (statesReadyTime === null) {
        return;
      }

      const feedbackExists = await checkFeedbackExists(
        documentId,
        'RESULT_PAGE_DEFAULT' as FeedbackSection,
      );

      if (!feedbackExists) {
        const timeSinceReady = Date.now() - statesReadyTime;
        const remainingDelay = Math.max(0, 15000 - timeSinceReady);

        if (remainingDelay > 0) {
          timeoutId = setTimeout(() => {
            setIsVisible(true);
          }, remainingDelay);
        } else {
          setIsVisible(true);
        }
      }
    };

    checkAndShowWidget();

    return () => {
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
    };
  }, [documentId, isSubmissionReady, statesReadyTime]);

  const onClose = async () => {
    if (step === 'select' && !selected) {
      if (documentId) {
        submitFeedback({
          documentId,
          section: 'RESULT_PAGE_DEFAULT' as FeedbackSection,
          rating: 'NO_RATING' as FeedbackRating,
          comment: undefined,
          targetedFeedback: [], // Empty for preflight
        }).catch((error) => {
          console.error('Failed to submit NO_RATING feedback on close:', error);
        });
      }

      setStep('minimized');
      return;
    }

    if (documentId) {
      try {
        if (
          selected &&
          (step === 'thankyou' || step === 'form' || step === 'email')
        ) {
          await submitFeedback({
            documentId,
            section: 'RESULT_PAGE_DEFAULT' as FeedbackSection,
            rating: selected,
            comment: feedback.trim() || undefined,
            targetedFeedback: [], // Empty for preflight
          });
        } else if (step === 'select' && !selected) {
          await submitFeedback({
            documentId,
            section: 'RESULT_PAGE_DEFAULT' as FeedbackSection,
            rating: 'NO_RATING' as FeedbackRating,
            comment: undefined,
            targetedFeedback: [], // Empty for preflight
          });
        }
      } catch (error) {
        console.error('Failed to submit feedback on close:', error);
      }
    }

    setStep('select');
    setSelected(null);
    setFeedback('');
    setEmail('');
    setEmailError('');
    setWantsEmailUpdates(false);
    setIsSubmitting(false);
    setIsVisible(false);
  };

  const onChatbotClick = () => {
    setStep('select');
  };

  const handleNoThatsAll = async () => {
    if (!selected || !documentId) {
      return;
    }

    try {
      await submitFeedback({
        documentId,
        section: 'RESULT_PAGE_DEFAULT' as FeedbackSection,
        rating: selected,
        comment: feedback.trim() || undefined,
        targetedFeedback: [], // Empty for preflight
      });

      if (selected === 'OUTSTANDING') {
        setStep('toast');
        setTimeout(() => {
          setStep('select');
          setSelected(null);
          setFeedback('');
          setEmail('');
          setEmailError('');
          setWantsEmailUpdates(false);
          setIsSubmitting(false);
          setIsVisible(false);
        }, 3500);
      } else {
        setStep('email');
      }
    } catch (error) {
      console.error('Failed to submit feedback:', error);
    }
  };

  const handleFormSubmit = async () => {
    if (!selected || !documentId) {
      return;
    }

    setIsSubmitting(true);
    try {
      await submitFeedback({
        documentId,
        section: 'RESULT_PAGE_DEFAULT' as FeedbackSection,
        rating: selected,
        comment: feedback.trim() || undefined,
        targetedFeedback: [], // Empty for preflight
      });

      if (selected === 'OUTSTANDING') {
        setStep('toast');
        setTimeout(() => {
          setStep('select');
          setSelected(null);
          setFeedback('');
          setEmail('');
          setEmailError('');
          setWantsEmailUpdates(false);
          setIsSubmitting(false);
          setIsVisible(false);
        }, 3500);
      } else {
        setStep('email');
      }
    } catch (error) {
      console.error('Failed to submit feedback with comment:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleEmailSubmit = async () => {
    if (!documentId) {
      return;
    }

    if (wantsEmailUpdates) {
      const trimmedEmail = email.trim();
      if (!trimmedEmail) {
        setEmailError(
          r('feedbackWidget.emailScreen.validationMessages.required'),
        );
        return;
      }
      if (!isValidEmail(trimmedEmail)) {
        setEmailError(getEmailValidationMessage(trimmedEmail));
        return;
      }
    }

    setEmailError('');
    setIsSubmitting(true);

    try {
      if (wantsEmailUpdates && email.trim()) {
        await requestFollowUp({
          documentId,
          email: email.trim(),
        });
      }

      setStep('toast');
      setTimeout(() => {
        setStep('select');
        setSelected(null);
        setFeedback('');
        setEmail('');
        setEmailError('');
        setWantsEmailUpdates(false);
        setIsSubmitting(false);
        setIsVisible(false);
      }, 3500);
    } catch (error) {
      console.error('Failed to submit email subscription:', error);
      setEmailError(
        r('feedbackWidget.emailScreen.validationMessages.submitError'),
      );
      setIsSubmitting(false);
    }
  };

  if (!isVisible || !documentId) {
    return null;
  }

  if (step === 'minimized') {
    return (
      <TrackedChatbotIcon
        onClick={onChatbotClick}
        onMouseEnter={() => setIsChatbotHovered(true)}
        onMouseLeave={() => setIsChatbotHovered(false)}
        trackingName="feedback_chatbot_icon_click"
        aria-label={r('feedbackWidget.chatbotIcon')}
        title="Share your feedback"
      >
        <Image
          src={
            isChatbotHovered ? '/feedback-active.svg' : '/feedback-silent.svg'
          }
          alt="Feedback"
          width={90}
          height={90}
        />
      </TrackedChatbotIcon>
    );
  }

  if (step === 'select') {
    return (
      <FeedbackContainer step="select">
        <TrackedCloseButton
          aria-label={r('feedbackWidget.closeButton')}
          onClick={onClose}
          trackingName="feedback_close_button_select"
        >
          <Image src={'/close-pp.svg'} alt="Close" width={16} height={16} />
        </TrackedCloseButton>
        <InnerSection>
          <Title>{r('feedbackWidget.title')}</Title>
          <OptionsColumn>
            {FEEDBACK_OPTIONS.map((option) => (
              <TrackedOptionButton
                key={option.value}
                type="button"
                selected={selected === option.value}
                onClick={() => {
                  setSelected(option.value);
                  setTimeout(() => setStep('thankyou'), 200);
                }}
                trackingName={`feedback_rating_${option.value.toLowerCase()}`}
              >
                <span style={{ fontSize: 20, marginBottom: 2 }}>
                  {option.emoji}
                </span>
                <span style={{ fontWeight: 500, width: '100%' }}>
                  {option.label}
                </span>
              </TrackedOptionButton>
            ))}
          </OptionsColumn>
        </InnerSection>
      </FeedbackContainer>
    );
  }

  if (step === 'thankyou' && selected === 'OUTSTANDING') {
    return (
      <FeedbackContainer step="other">
        <TrackedCloseButton
          aria-label={r('feedbackWidget.closeButton')}
          onClick={onClose}
          trackingName="feedback_close_button_thankyou_outstanding"
        >
          <Image src={'/close-pp.svg'} alt="Close" width={16} height={16} />
        </TrackedCloseButton>
        <div style={{ fontSize: 20, marginBottom: 8 }}>🎉</div>
        <ThankYouTitle>{r('feedbackWidget.thankYouTitle')}</ThankYouTitle>
        <ThankYouSubtitle>
          {r('feedbackWidget.thankYouSubtitle.outstanding')}
        </ThankYouSubtitle>
        <TrackedThankYouButton
          onClick={() => setStep('form')}
          trackingName="feedback_yes_add_feedback_outstanding"
        >
          {r('feedbackWidget.buttons.yesAddFeedback')}
        </TrackedThankYouButton>
        <TrackedThankYouSecondary
          onClick={() => handleNoThatsAll()}
          trackingName="feedback_no_thats_all_outstanding"
        >
          {r('feedbackWidget.buttons.noThatsAll')}
        </TrackedThankYouSecondary>
      </FeedbackContainer>
    );
  }

  if (step === 'thankyou' && selected === 'ACCEPTABLE') {
    return (
      <FeedbackContainer step="other">
        <TrackedCloseButton
          aria-label={r('feedbackWidget.closeButton')}
          onClick={onClose}
          trackingName="feedback_close_button_thankyou_acceptable"
        >
          <Image src={'/close-pp.svg'} alt="Close" width={16} height={16} />
        </TrackedCloseButton>
        <div style={{ fontSize: 20, marginBottom: 8 }}>🙏</div>
        <ThankYouTitle>{r('feedbackWidget.thankYouTitle')}</ThankYouTitle>
        <ThankYouSubtitle>
          {r('feedbackWidget.thankYouSubtitle.acceptable')}
        </ThankYouSubtitle>
        <TrackedThankYouButton
          onClick={() => setStep('form')}
          trackingName="feedback_yes_add_feedback_acceptable"
        >
          {r('feedbackWidget.buttons.yesAddFeedback')}
        </TrackedThankYouButton>
        <TrackedThankYouSecondary
          onClick={() => handleNoThatsAll()}
          trackingName="feedback_no_thats_all_acceptable"
        >
          {r('feedbackWidget.buttons.noThatsAll')}
        </TrackedThankYouSecondary>
      </FeedbackContainer>
    );
  }

  if (step === 'thankyou' && selected === 'NOT_ACCEPTABLE') {
    return (
      <FeedbackContainer step="other">
        <TrackedCloseButton
          aria-label={r('feedbackWidget.closeButton')}
          onClick={onClose}
          trackingName="feedback_close_button_thankyou_not_acceptable"
        >
          <Image src={'/close-pp.svg'} alt="Close" width={16} height={16} />
        </TrackedCloseButton>
        <div style={{ fontSize: 20, marginBottom: 8 }}>🙏</div>
        <ThankYouTitle>{r('feedbackWidget.thankYouTitle')}</ThankYouTitle>
        <ThankYouSubtitle>
          {r('feedbackWidget.thankYouSubtitle.notAcceptable')}
        </ThankYouSubtitle>
        <TrackedThankYouButton
          onClick={() => setStep('form')}
          trackingName="feedback_yes_add_feedback_not_acceptable"
        >
          {r('feedbackWidget.buttons.yesAddFeedback')}
        </TrackedThankYouButton>
        <TrackedThankYouSecondary
          onClick={() => handleNoThatsAll()}
          trackingName="feedback_no_thats_all_not_acceptable"
        >
          {r('feedbackWidget.buttons.noThatsAll')}
        </TrackedThankYouSecondary>
      </FeedbackContainer>
    );
  }

  if (step === 'form') {
    const formContent = selected
      ? getFeedbackFormContent(selected)
      : { title: '', label: '', placeholder: '' };

    return (
      <FeedbackContainer step="other">
        <TrackedCloseButton
          aria-label={r('feedbackWidget.closeButton')}
          onClick={onClose}
          trackingName="feedback_close_button_form"
        >
          <Image src={'/close-pp.svg'} alt="Close" width={16} height={16} />
        </TrackedCloseButton>
        <FeedbackFormTitle>{formContent.title}</FeedbackFormTitle>
        <FeedbackFormLabel>{formContent.label}</FeedbackFormLabel>
        <FeedbackTextarea
          placeholder={formContent.placeholder}
          value={feedback}
          onChange={(e) => setFeedback(e.target.value)}
        />
        <TrackedSubmitButton
          onClick={handleFormSubmit}
          disabled={isSubmitting}
          trackingName="feedback_submit_comment"
        >
          {isSubmitting
            ? r('feedbackWidget.buttons.submitting')
            : r('feedbackWidget.buttons.submitFeedback')}
        </TrackedSubmitButton>
      </FeedbackContainer>
    );
  }

  if (step === 'email') {
    return (
      <FeedbackContainer step="email">
        <TrackedCloseButton
          aria-label={r('feedbackWidget.closeButton')}
          onClick={onClose}
          trackingName="feedback_close_button_email"
        >
          <Image src={'/close-pp.svg'} alt="Close" width={16} height={16} />
        </TrackedCloseButton>
        <EmailContainer>
          <EmailIcon>
            <svg
              width="32"
              height="32"
              viewBox="0 0 24 24"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9"
                stroke="url(#notificationGradient)"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
                fill="url(#notificationFillGradient)"
              />
              <path
                d="M13.73 21a2 2 0 0 1-3.46 0"
                stroke="url(#notificationGradient)"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
              <circle
                cx="18"
                cy="6"
                r="3"
                fill="url(#dotGradient)"
                stroke="white"
                strokeWidth="1"
              />
              <defs>
                <linearGradient
                  id="notificationGradient"
                  x1="0%"
                  y1="0%"
                  x2="100%"
                  y2="100%"
                >
                  <stop offset="0%" stopColor="#0062FF" />
                  <stop offset="100%" stopColor="#3B82F6" />
                </linearGradient>
                <linearGradient
                  id="notificationFillGradient"
                  x1="0%"
                  y1="0%"
                  x2="100%"
                  y2="100%"
                >
                  <stop offset="0%" stopColor="rgba(0, 98, 255, 0.1)" />
                  <stop offset="100%" stopColor="rgba(59, 130, 246, 0.05)" />
                </linearGradient>
                <linearGradient
                  id="dotGradient"
                  x1="0%"
                  y1="0%"
                  x2="100%"
                  y2="100%"
                >
                  <stop offset="0%" stopColor="#FF4444" />
                  <stop offset="100%" stopColor="#FF6B6B" />
                </linearGradient>
              </defs>
            </svg>
          </EmailIcon>
          <EmailTitle>{r('feedbackWidget.emailScreen.title')}</EmailTitle>
          <EmailSubtitle>
            {r('feedbackWidget.emailScreen.subtitle')}
          </EmailSubtitle>

          <EmailInput
            type="email"
            placeholder={r('feedbackWidget.emailScreen.emailPlaceholder')}
            value={email}
            onChange={(e) => {
              setEmail(e.target.value);
              if (emailError) {
                setEmailError('');
              }
            }}
            state={
              emailError || (email.trim() && !isValidEmail(email.trim()))
                ? 'error'
                : undefined
            }
          />

          {(emailError || (email.trim() && !isValidEmail(email.trim()))) && (
            <EmailErrorMessage>
              {emailError ||
                r('feedbackWidget.emailScreen.validationMessages.invalid')}
            </EmailErrorMessage>
          )}

          <CheckboxContainer>
            <CheckboxInputNew
              disabled={false}
              name="email-updates"
              label={
                <span
                  style={{
                    fontSize: 12,
                    color: 'rgba(93, 106, 129, 1)',
                    lineHeight: '16px',
                    fontWeight: 500,
                  }}
                >
                  {r('feedbackWidget.emailScreen.checkboxLabel')}
                </span>
              }
              props={{
                type: 'checkbox',
                checked: wantsEmailUpdates,
                onChange: (e: React.ChangeEvent<HTMLInputElement>) =>
                  setWantsEmailUpdates(e.target.checked),
              }}
              width={16}
              height={16}
            />
          </CheckboxContainer>

          {getSubmitButtonHelperText() && (
            <EmailHelperText>{getSubmitButtonHelperText()}</EmailHelperText>
          )}

          <EmailButtonContainer>
            <TrackedEmailSubmitButton
              onClick={handleEmailSubmit}
              disabled={isEmailSubmissionDisabled()}
              trackingName="feedback_email_submit"
            >
              {isSubmitting
                ? r('feedbackWidget.emailScreen.submittingButton')
                : r('feedbackWidget.emailScreen.submitButton')}
            </TrackedEmailSubmitButton>
          </EmailButtonContainer>
        </EmailContainer>
      </FeedbackContainer>
    );
  }

  if (step === 'toast') {
    return (
      <ToastContainer>
        <span style={{ fontSize: 20, marginRight: 5 }}>✅</span>
        {r('feedbackWidget.toast.message')}
        <TrackedToastClose
          aria-label={r('feedbackWidget.closeButton')}
          onClick={onClose}
          trackingName="feedback_toast_close"
        >
          ×
        </TrackedToastClose>
      </ToastContainer>
    );
  }

  return null;
}
